import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { BsCheck2All, BsDatabaseCheck, BsPencilSquare } from "react-icons/bs";
import {
  DTOCatalogosRespaldos,
  DTODetalleBitacoraRespaldo,
  DTOEquipoConRespaldos,
  DTOReporteMensualRespaldos,
} from "../../../DTO/Inventario/DTORespaldos";
import RespaldosService from "../../../Services/Catalogos/RespaldosService";
import { MsgInformativo } from "../../../Utils/Toast/msgInformativo";
import "./Respaldos.css";

const fechaHoy = () => {
  const hoy = new Date();
  const local = new Date(hoy.getTime() - hoy.getTimezoneOffset() * 60000);
  return local.toISOString().substring(0, 10);
};

const mesActual = () => fechaHoy().substring(0, 7);

const obtenerMensajeError = (error: unknown) => {
  const e = error as { response?: { data?: { mensaje?: string } }; message?: string };
  return e.response?.data?.mensaje || e.message || "Ocurrió un error inesperado.";
};

const colorEstado = (codigo: string) => {
  if (codigo === "COMPLETO") return "success";
  if (codigo === "PENDIENTE") return "warning";
  if (codigo === "PROGRAMADO") return "secondary";
  return "danger";
};

const BitacoraRespaldos = () => {
  const [equipos, setEquipos] = useState<DTOEquipoConRespaldos[]>([]);
  const [catalogos, setCatalogos] = useState<DTOCatalogosRespaldos>({ frecuencias: [], estados: [] });
  const [idEquipo, setIdEquipo] = useState(0);
  const [periodo, setPeriodo] = useState(mesActual());
  const [fechaSeleccionada, setFechaSeleccionada] = useState(fechaHoy());
  const [reporte, setReporte] = useState<DTOReporteMensualRespaldos | null>(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [captura, setCaptura] = useState<DTODetalleBitacoraRespaldo | null>(null);
  const [idEstatus, setIdEstatus] = useState(0);
  const [observaciones, setObservaciones] = useState("");
  const [accionTomada, setAccionTomada] = useState("");
  const [showMsg, setShowMsg] = useState(false);
  const [msg, setMsg] = useState("");
  const [header, setHeader] = useState("");
  const [msgColor, setMsgColor] = useState("primary");

  const mostrarMensaje = (titulo: string, mensaje: string, color: string) => {
    setHeader(titulo);
    setMsg(mensaje);
    setMsgColor(color);
    setShowMsg(true);
  };

  useEffect(() => {
    const inicializar = async () => {
      setCargando(true);
      try {
        const [respuestaEquipos, respuestaCatalogos] = await Promise.all([
          RespaldosService.obtenerEquipos(),
          RespaldosService.obtenerCatalogos(),
        ]);
        const lista = respuestaEquipos.data || [];
        setEquipos(lista);
        setCatalogos(respuestaCatalogos.data);
        if (lista.length > 0) setIdEquipo(lista[0].idEquipo);
      } catch (error) {
        mostrarMensaje("Error", obtenerMensajeError(error), "danger");
      } finally {
        setCargando(false);
      }
    };
    void inicializar();
  }, []);

  const cargarReporte = async () => {
    if (!idEquipo || !periodo) return;
    const [anio, mes] = periodo.split("-").map(Number);
    setCargando(true);
    try {
      const respuesta = await RespaldosService.obtenerReporteMensual(idEquipo, anio, mes);
      setReporte(respuesta.data);
    } catch (error) {
      mostrarMensaje("Error", obtenerMensajeError(error), "danger");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (idEquipo) void cargarReporte();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEquipo, periodo]);

  const cambiarPeriodo = (nuevoPeriodo: string) => {
    setPeriodo(nuevoPeriodo);
    setFechaSeleccionada(`${nuevoPeriodo}-01`);
  };

  const detalleDia = useMemo(
    () => (reporte?.detalle || []).filter((x) => x.fechaProgramada.substring(0, 10) === fechaSeleccionada),
    [reporte, fechaSeleccionada]
  );

  const abrirCaptura = (detalle: DTODetalleBitacoraRespaldo) => {
    setCaptura(detalle);
    setIdEstatus(
      detalle.idEstatusRespaldo || catalogos.estados.find((x) => x.codigo === "COMPLETO")?.idEstatusRespaldo || 0
    );
    setObservaciones(detalle.observaciones || "");
    setAccionTomada(detalle.accionTomada || "");
  };

  const guardarCaptura = async (event: FormEvent) => {
    event.preventDefault();
    if (!captura || !idEstatus) {
      mostrarMensaje("Informativo", "Selecciona un estado.", "warning");
      return;
    }
    setGuardando(true);
    try {
      const respuesta = await RespaldosService.guardarBitacora({
        idRespaldo: captura.idRespaldo,
        fechaEjecucion: fechaSeleccionada,
        idEstatusRespaldo: idEstatus,
        observaciones: observaciones.trim() || null,
        accionTomada: accionTomada.trim() || null,
      });
      setCaptura(null);
      mostrarMensaje("Éxito", respuesta.data.mensaje, "success");
      await cargarReporte();
    } catch (error) {
      mostrarMensaje("Error", obtenerMensajeError(error), "danger");
    } finally {
      setGuardando(false);
    }
  };

  const marcarPendientesCompletos = async () => {
    const pendientes = detalleDia.filter((x) => x.codigoEstado === "PENDIENTE");
    const completo = catalogos.estados.find((x) => x.codigo === "COMPLETO");
    if (!completo || pendientes.length === 0) {
      mostrarMensaje("Informativo", "No hay respaldos pendientes para esta fecha.", "warning");
      return;
    }
    if (!window.confirm(`¿Marcar ${pendientes.length} respaldo(s) como completos?`)) return;

    setGuardando(true);
    try {
      await Promise.all(
        pendientes.map((x) =>
          RespaldosService.guardarBitacora({
            idRespaldo: x.idRespaldo,
            fechaEjecucion: fechaSeleccionada,
            idEstatusRespaldo: completo.idEstatusRespaldo,
          })
        )
      );
      mostrarMensaje("Éxito", "Respaldos marcados como completos.", "success");
      await cargarReporte();
    } catch (error) {
      mostrarMensaje("Error", obtenerMensajeError(error), "danger");
    } finally {
      setGuardando(false);
    }
  };

  const resumen = reporte?.resumen;

  return (
    <div className="bitacora-respaldos container-fluid py-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div><h5 className="mb-1"><BsDatabaseCheck className="me-2 text-primary" />Bitácora de respaldos</h5><small className="text-muted">Captura diaria y seguimiento por servidor</small></div>
      </div>

      <Card className="border-0 shadow-sm mb-3"><Card.Body><Row className="g-3 align-items-end">
        <Col lg={5} md={6}><Form.Label>Servidor</Form.Label><Form.Select value={idEquipo} onChange={(e) => setIdEquipo(Number(e.target.value))}>{equipos.map((x) => <option key={x.idEquipo} value={x.idEquipo}>{x.nombreEquipo} ({x.cantidadRespaldos})</option>)}</Form.Select></Col>
        <Col lg={3} md={3}><Form.Label>Mes del reporte</Form.Label><Form.Control type="month" value={periodo} onChange={(e) => cambiarPeriodo(e.target.value)} /></Col>
        <Col lg={4} md={3}><Form.Label>Fecha de captura</Form.Label><Form.Control type="date" min={`${periodo}-01`} max={`${periodo}-31`} value={fechaSeleccionada} onChange={(e) => setFechaSeleccionada(e.target.value)} /></Col>
      </Row></Card.Body></Card>

      {resumen && <Row className="g-2 g-md-3 mb-3 resumen-respaldos">
        {[
          ["Programados", resumen.totalProgramados, "primary"],
          ["Completos", resumen.totalCompletos, "success"],
          ["Con incidencia", resumen.totalConIncidencia, "danger"],
          ["Pendientes", resumen.totalPendientes, "warning"],
          ["Futuros", resumen.totalFuturos, "secondary"],
        ].map(([titulo, valor, color]) => <Col xl key={String(titulo)} xs={6} md={4}><Card className="border-0 shadow-sm h-100"><Card.Body className="text-center"><div className="small text-muted">{titulo}</div><div className={`fs-3 fw-bold text-${color}`}>{valor}</div></Card.Body></Card></Col>)}
      </Row>}

      <Card className="border-0 shadow-sm mb-3"><Card.Body>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"><div><strong>Respaldos del {new Date(`${fechaSeleccionada}T12:00:00`).toLocaleDateString("es-MX", { dateStyle: "long" })}</strong><div className="small text-muted">{detalleDia.length} ejecución(es) programada(s)</div></div><Button onClick={marcarPendientesCompletos} disabled={guardando || detalleDia.every((x) => x.codigoEstado !== "PENDIENTE")}><BsCheck2All className="me-2" />Marcar pendientes completos</Button></div>

        {cargando ? <div className="text-center py-5"><Spinner animation="border" /></div> : detalleDia.length === 0 ? <div className="respaldo-vacio">No hay respaldos programados para esta fecha.</div> : <>
          <div className="tabla-respaldos-escritorio"><Table responsive hover className="align-middle"><thead><tr><th>Hora</th><th>Respaldo</th><th>Frecuencia</th><th>Estado</th><th>Observaciones</th><th /></tr></thead><tbody>{detalleDia.map((x) => <tr key={`${x.idRespaldo}-${x.fechaProgramada}`}><td>{x.horaProgramada.substring(0, 5)}</td><td className="fw-semibold">{x.nombreRespaldo}</td><td>{x.frecuencia}</td><td><Badge bg={colorEstado(x.codigoEstado)} text={x.codigoEstado === "PENDIENTE" ? "dark" : undefined}>{x.estado}</Badge></td><td>{x.observaciones || "—"}</td><td className="text-end"><Button size="sm" variant="outline-primary" disabled={x.codigoEstado === "PROGRAMADO"} onClick={() => abrirCaptura(x)}><BsPencilSquare className="me-1" />Capturar</Button></td></tr>)}</tbody></Table></div>
          <div className="lista-respaldos-movil">{detalleDia.map((x) => <div className="respaldo-config-card" key={`${x.idRespaldo}-${x.fechaProgramada}`}><div className="d-flex justify-content-between gap-2"><strong>{x.nombreRespaldo}</strong><Badge bg={colorEstado(x.codigoEstado)} text={x.codigoEstado === "PENDIENTE" ? "dark" : undefined}>{x.estado}</Badge></div><div className="small text-muted mt-2">{x.horaProgramada.substring(0, 5)} · {x.frecuencia}</div><div className="small mt-2">{x.observaciones || "Sin observaciones"}</div><Button className="mt-3 w-100" size="sm" variant="outline-primary" disabled={x.codigoEstado === "PROGRAMADO"} onClick={() => abrirCaptura(x)}><BsPencilSquare className="me-1" />Capturar resultado</Button></div>)}</div>
        </>}
      </Card.Body></Card>

      {reporte && reporte.conteos.length > 0 && <Card className="border-0 shadow-sm"><Card.Body><strong>Totales por estado del mes</strong><div className="d-flex flex-wrap gap-2 mt-3">{reporte.conteos.map((x) => <Badge className="px-3 py-2" bg={colorEstado(x.codigoEstado)} text={x.codigoEstado === "PENDIENTE" ? "dark" : undefined} key={x.codigoEstado}>{x.estado}: {x.cantidad}</Badge>)}</div></Card.Body></Card>}

      <Modal show={captura !== null} onHide={() => !guardando && setCaptura(null)} centered fullscreen="sm-down">
        <Form onSubmit={guardarCaptura}><Modal.Header closeButton><Modal.Title>Resultado del respaldo</Modal.Title></Modal.Header><Modal.Body>
          <div className="mb-3"><strong>{captura?.nombreRespaldo}</strong><div className="small text-muted">{fechaSeleccionada} · {captura?.horaProgramada.substring(0, 5)}</div></div>
          <Form.Group className="mb-3"><Form.Label>Estado *</Form.Label><Form.Select required value={idEstatus} onChange={(e) => setIdEstatus(Number(e.target.value))}>{catalogos.estados.map((x) => <option key={x.idEstatusRespaldo} value={x.idEstatusRespaldo}>{x.estatus}</option>)}</Form.Select></Form.Group>
          <Form.Group className="mb-3"><Form.Label>Observaciones</Form.Label><Form.Control as="textarea" rows={3} maxLength={1000} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></Form.Group>
          <Form.Group><Form.Label>Acción tomada</Form.Label><Form.Control as="textarea" rows={3} maxLength={1000} value={accionTomada} onChange={(e) => setAccionTomada(e.target.value)} /></Form.Group>
        </Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setCaptura(null)} disabled={guardando}>Cancelar</Button><Button type="submit" disabled={guardando}>{guardando ? <><Spinner size="sm" className="me-2" />Guardando...</> : "Guardar resultado"}</Button></Modal.Footer></Form>
      </Modal>

      <MsgInformativo show={showMsg} header={header} msg={msg} msgColor={msgColor} time={3500} closeToast={() => setShowMsg(false)} />
    </div>
  );
};

export default BitacoraRespaldos;
